/**
 * Public-holiday surcharge: percent of GST-inclusive bill base on manually listed dates.
 * Amount is snapshotted onto the order at place/update; totals read the stored field.
 */
import type { OrderType } from '../types';
import { dateKeyInTimeZone } from './businessHours';

export const DEFAULT_HOLIDAY_SURCHARGE_LABEL = 'Public holiday surcharge';

/** Order types that receive the holiday surcharge. Uber/DoorDash uses platform markup instead. */
export const HOLIDAY_SURCHARGE_ORDER_TYPES: ReadonlySet<OrderType> = new Set([
  'dine-in',
  'takeaway',
  'delivery',
  'buffet',
  'online',
  'event',
]);

export type HolidaySurchargeConfig = {
  enabled: boolean;
  percent: number;
  dates: string[];
  label?: string;
};

const DATE_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Normalize a calendar date to YYYY-MM-DD, or null if invalid. */
export function normalizeHolidayDateKey(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  const match = DATE_KEY_RE.exec(trimmed);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const utc = new Date(Date.UTC(year, month - 1, day));
  if (utc.getUTCFullYear() !== year || utc.getUTCMonth() !== month - 1 || utc.getUTCDate() !== day) {
    return null;
  }
  return `${match[1]}-${match[2]}-${match[3]}`;
}

export function isHolidaySurchargeDate(dateKey: string, dates: readonly string[] | undefined): boolean {
  if (!dateKey || !Array.isArray(dates) || dates.length === 0) return false;
  const normalized = normalizeHolidayDateKey(dateKey);
  if (!normalized) return false;
  for (const d of dates) {
    if (normalizeHolidayDateKey(d) === normalized) return true;
  }
  return false;
}

export function computeHolidaySurchargeAmount(baseInclGst: number, percent: number): number {
  const base = Number(baseInclGst);
  const pct = Number(percent);
  if (!Number.isFinite(base) || base <= 0) return 0;
  if (!Number.isFinite(pct) || pct <= 0) return 0;
  return round2(base * (pct / 100));
}

export function normalizeHolidaySurchargeConfig(
  raw: unknown
): HolidaySurchargeConfig | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const obj = raw as Record<string, unknown>;
  const percent = Math.max(0, Number(obj.percent) || 0);
  const dates = Array.isArray(obj.dates)
    ? Array.from(
        new Set(
          obj.dates
            .map((d) => normalizeHolidayDateKey(d))
            .filter((d): d is string => !!d)
        )
      ).sort()
    : [];
  const label =
    typeof obj.label === 'string' && obj.label.trim()
      ? obj.label.trim().slice(0, 80)
      : undefined;
  return {
    enabled: obj.enabled === true,
    percent,
    dates,
    ...(label ? { label } : {}),
  };
}

export function holidaySurchargeLabel(config?: HolidaySurchargeConfig | null): string {
  const label = config?.label?.trim();
  return label || DEFAULT_HOLIDAY_SURCHARGE_LABEL;
}

export function shouldApplyHolidaySurcharge(
  orderType: OrderType | string | undefined,
  config: HolidaySurchargeConfig | null | undefined,
  now: Date = new Date(),
  timeZone: string = 'Pacific/Auckland'
): boolean {
  if (!config?.enabled) return false;
  if (!orderType || !HOLIDAY_SURCHARGE_ORDER_TYPES.has(orderType as OrderType)) return false;
  const percent = Number(config.percent) || 0;
  if (percent <= 0) return false;
  const tz = (timeZone || 'Pacific/Auckland').trim() || 'Pacific/Auckland';
  const dateKey = dateKeyInTimeZone(now, tz);
  return isHolidaySurchargeDate(dateKey, config.dates);
}

/**
 * Resolve surcharge dollars for a bill base (items after discounts + delivery).
 * Returns null when surcharge does not apply (caller should omit/clear stored fields).
 */
export function resolveHolidaySurcharge(input: {
  orderType: OrderType | string | undefined;
  baseInclGst: number;
  config: HolidaySurchargeConfig | null | undefined;
  now?: Date;
  timeZone?: string;
}): { amount: number; percent: number; label: string } | null {
  const now = input.now ?? new Date();
  const timeZone = input.timeZone ?? 'Pacific/Auckland';
  if (!shouldApplyHolidaySurcharge(input.orderType, input.config, now, timeZone)) {
    return null;
  }
  const percent = Number(input.config!.percent) || 0;
  const amount = computeHolidaySurchargeAmount(input.baseInclGst, percent);
  if (amount <= 0) return null;
  return {
    amount,
    percent,
    label: holidaySurchargeLabel(input.config),
  };
}

/** Bill base for surcharge: items after discounts + delivery (GST inclusive). */
export function holidaySurchargeBaseFromParts(input: {
  itemsNetInclGst: number;
  deliveryCharge?: number;
}): number {
  const items = Math.max(0, Number(input.itemsNetInclGst) || 0);
  const delivery = Math.max(0, Number(input.deliveryCharge) || 0);
  return round2(items + delivery);
}

/**
 * Snapshot holiday fields for an order given GST-inclusive base (items after discounts + delivery).
 * When not applicable, returns zeros so callers can overwrite stale Firestore values.
 */
export function holidaySurchargeSnapshotFields(input: {
  orderType: OrderType | string | undefined;
  baseInclGst: number;
  config: HolidaySurchargeConfig | null | undefined;
  now?: Date;
  timeZone?: string;
}): { holidaySurchargeAmount: number; holidaySurchargePercent: number } {
  const resolved = resolveHolidaySurcharge(input);
  if (!resolved) {
    return { holidaySurchargeAmount: 0, holidaySurchargePercent: 0 };
  }
  return {
    holidaySurchargeAmount: resolved.amount,
    holidaySurchargePercent: resolved.percent,
  };
}
